import './assets/main.css'
import './assets/css/pwa.css'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin } from '@tanstack/vue-query'
import PrimeVue from 'primevue/config'
import ToastService from 'primevue/toastservice'
// import ConfirmationService from 'primevue/confirmationservice'
// import DialogService from 'primevue/dialogservice'
import App from './App.vue'
import router from './router'
// import Lara from '@primeuix/themes/lara'
import CustomTheme from './assets/theme/primevue'
import './services/pusher.ts'
import { setupPaymentLinkHandling } from './utils/externalLinks.js'
import { initPaymentReturnHandling } from './utils/paymentReturnHandler.js'
import { initPaymentReturnHandling as initServiceWorkerCleanup } from './utils/serviceWorkerCleanup.js'
import { registerPushNotificationServiceWorker, registerForPushNotifications, requestNotificationPermission } from './pwa'

const app = createApp(App)

app.use(createPinia())
app.use(router)
app.use(VueQueryPlugin)
app.use(PrimeVue, {
  unstyled: true,
  pt: CustomTheme,
  darkModeSelector: 'system',
  ripple: true,
  inputStyle: 'filled'
})
app.use(ToastService)
// app.use(ConfirmationService)
// app.use(DialogService)
app.mount('#app')

// Import route helper to check if we're in an admin route
import { isAdminPaymentsRoute } from './utils/routeHelper'

// Fix for mobile viewport height issues
const setVH = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};

// Set initial viewport height
setVH();

// Update viewport height on resize and orientation change
window.addEventListener('resize', setVH);
window.addEventListener('orientationchange', () => {
  setTimeout(setVH, 100);
});

// Handle payment return viewport issues
const handlePaymentReturn = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const isPaymentReturn = urlParams.has('payment_success') ||
                         urlParams.has('payment_failed') ||
                         urlParams.has('reference') ||
                         urlParams.has('trxref') ||
                         urlParams.has('transaction_id');

  if (isPaymentReturn) {
    // Force viewport recalculation after payment return
    setTimeout(() => {
      setVH();
      // Scroll to top to prevent any white space
      window.scrollTo(0, 0);
    }, 100);
  }
};

// Check for payment return on page load
handlePaymentReturn();

// Listen for popstate events (back/forward navigation)
window.addEventListener('popstate', () => {
  setTimeout(handlePaymentReturn, 50);
});

// Initialize payment link handling for PWA
document.addEventListener('DOMContentLoaded', () => {
  // Skip payment handling initialization for admin routes
  if (isAdminPaymentsRoute()) {
    return;
  }

  // Setup external link handling for payment links
  setupPaymentLinkHandling();

  // Initialize payment return handling to fix endless loading after payment
  initPaymentReturnHandling({
    onPaymentReturn: (result: { success: boolean }) => {
      const toast = app.config.globalProperties.$toast;
      if (toast) {
        if (result.success) {
          toast.add({
            severity: 'success',
            summary: 'Payment Successful',
            detail: 'Your payment has been processed successfully',
            life: 5000
          });
        } else {
          toast.add({
            severity: 'error',
            summary: 'Payment Failed',
            detail: 'There was an issue processing your payment',
            life: 5000
          });
        }
      }
    }
  });

  // Initialize service worker cleanup for payment returns
  initServiceWorkerCleanup();

  // Initialize push notifications
  const initPushNotifications = async () => {
    try {
      // Register push notification service worker
      await registerPushNotificationServiceWorker();

      // Check if notifications are already granted
      const permission = await requestNotificationPermission();

      // If permission is granted, register for push notifications
      if (permission === 'granted') {
        const registered = await registerForPushNotifications();
      }
    } catch (error) {
    }
  };

  // Initialize push notifications
  initPushNotifications();

  // Check if we need to redirect after a payment reload
  const checkPaymentRedirect = () => {
    try {
      // Check if we have a stored redirect URL from payment return
      const redirectUrl = sessionStorage.getItem('payment_redirect_url');
      if (redirectUrl) {

        // Clear the stored URL to prevent future redirects
        sessionStorage.removeItem('payment_redirect_url');

        // Wait a moment for the app to fully initialize before redirecting
        setTimeout(() => {
          // Use router to navigate if it's an internal URL
          if (redirectUrl.startsWith(window.location.origin) || redirectUrl.startsWith('/')) {
            const url = new URL(redirectUrl, window.location.origin);
            const path = url.pathname + url.search;
            router.push(path);
          } else {
            // For external URLs, use window.location
            window.location.href = redirectUrl;
          }
        }, 500);
      }
    } catch (error) {
    }
  };

  // Run the redirect check after app is mounted
  setTimeout(checkPaymentRedirect, 1000);
});

